import { DEFAULT_EIGHTD_MAPPING } from "./stage-link";
import { applyTimeline } from "./timeline";
import type {
  AuditEntry,
  ClauseMapping,
  ContainmentAction,
  EightDStage,
  EvidenceItem,
  EvidenceType,
  FinancialProfile,
  PfmeaRow,
  ProblemRecord,
  Program,
  ReadAcrossItem,
  RecordStatus,
  RecoveryAction,
  RiskLevel,
  SeriesPoint,
  StageStatus,
  WarrantyDataSet,
  WorkflowStage,
} from "./types";

export const DATA_VERSION = 5;

const MONTHS = [
  "Sep 25",
  "Oct 25",
  "Nov 25",
  "Dec 25",
  "Jan 26",
  "Feb 26",
  "Mar 26",
  "Apr 26",
  "May 26",
  "Jun 26",
  "Jul 26",
  "Aug 26",
];

/* deterministic pseudo-random so the demo dataset is stable */
function rng(seedText: string) {
  let h = 2166136261;
  for (let i = 0; i < seedText.length; i++) {
    h ^= seedText.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  let s = h >>> 0;
  return () => {
    s += 0x6d2b79f5;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const iso = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d)).toISOString();

export const WORKFLOW_BLUEPRINT: {
  name: string;
  purpose: string;
  clause: string;
  evidence: string[];
}[] = [
  {
    name: "Connected Signal",
    purpose:
      "Aggregate warranty, telematics and dealer-comment signals are connected and confirmed as a genuine emerging failure mode.",
    clause: "IATF 16949 9.1.1.1 — Monitoring and measurement of manufacturing processes",
    evidence: ["Aggregate claims signal package", "Signal confirmation memo"],
  },
  {
    name: "Management Top 5",
    purpose:
      "Signal is ranked against the plant/program portfolio and formally accepted into the management Top 5 review.",
    clause: "IATF 16949 5.1.1.1 — Corporate responsibility and management review",
    evidence: ["Top 5 ranking sheet", "Management review minutes"],
  },
  {
    name: "Connected Evidence",
    purpose:
      "Evidence set is assembled: returned-part analysis, claim-level detail, process data and supplier lot data.",
    clause: "ISO 9001 7.5.3 — Control of documented information",
    evidence: ["Returned-part aggregate analysis", "Process capability extract"],
  },
  {
    name: "Governed Problem Record",
    purpose:
      "A single governed record is opened with owner, champion, scope, target close date and standard clause mapping.",
    clause: "IATF 16949 10.2.3 — Problem solving",
    evidence: ["Problem statement (is / is-not)", "Governance assignment record"],
  },
  {
    name: "Containment",
    purpose:
      "Interim containment protects the customer at plant, in-transit, dealer stock and supplier locations with verified effectiveness.",
    clause: "ISO 9001 8.7 / IATF 16949 8.7.1.1 — Control of nonconforming output",
    evidence: ["Containment work instruction", "Containment effectiveness check"],
  },
  {
    name: "Root Cause & Corrective Action",
    purpose:
      "Technical and systemic root causes are proven with data, and corrective actions are selected and validated.",
    clause: "ISO 9001 10.2.2 / IATF 16949 10.2.3 — Corrective action",
    evidence: ["Root cause proof package", "Corrective action validation report"],
  },
  {
    name: "Engineering / Process Change",
    purpose:
      "Design or process change is authorised, validated and released through the change control process.",
    clause: "IATF 16949 8.5.6.1 — Control of changes (supplemental)",
    evidence: ["Change request approval", "Validation test report"],
  },
  {
    name: "Breakpoint & Implementation",
    purpose:
      "Serial breakpoint is defined and confirmed in production and at the supplier, with traceable effective date.",
    clause: "IATF 16949 8.5.6.1 / 8.3.6 — Change implementation and traceability",
    evidence: ["Breakpoint confirmation record", "Implementation audit"],
  },
  {
    name: "PFMEA / Control Plan Update",
    purpose:
      "PFMEA ratings and the control plan are updated to reflect the new controls, including missed-rating rationale.",
    clause: "IATF 16949 8.3.5.2 / 8.5.1.1 — PFMEA and control plan",
    evidence: ["Revised PFMEA sheet", "Revised control plan"],
  },
  {
    name: "Effectiveness & Prevention",
    purpose:
      "Post-breakpoint claims are measured against the target reduction and prevention actions are institutionalised.",
    clause: "ISO 9001 9.1.3 / IATF 16949 10.2.4 — Analysis and error-proofing",
    evidence: ["Post-breakpoint C/1000 analysis", "Lessons-learned entry"],
  },
  {
    name: "Read-Across & Closure",
    purpose:
      "Similar platforms and components are screened, actions assigned, and the record is closed with governance sign-off.",
    clause: "IATF 16949 10.2.3 / 10.3.1 — Problem solving and continual improvement",
    evidence: ["Read-across screening matrix", "Closure sign-off"],
  },
];

const EIGHT_D = [
  {
    id: "D1",
    title: "Establish the Team",
    objective: "Cross-functional team with the right competence and a champion.",
  },
  {
    id: "D2",
    title: "Describe the Problem",
    objective: "Quantified is / is-not problem statement using aggregate field data.",
  },
  {
    id: "D3",
    title: "Interim Containment",
    objective: "Protect the customer until permanent actions are verified.",
  },
  {
    id: "D4",
    title: "Root Cause Analysis",
    objective: "Prove occurrence, escape and systemic root causes.",
  },
  {
    id: "D5",
    title: "Permanent Corrective Actions",
    objective: "Select and validate actions that remove the root cause.",
  },
  {
    id: "D6",
    title: "Implement & Validate",
    objective: "Implement at breakpoint and validate with production data.",
  },
  {
    id: "D7",
    title: "Prevent Recurrence",
    objective: "Update PFMEA, control plan, standards and read-across.",
  },
  {
    id: "D8",
    title: "Recognise the Team",
    objective: "Close out, capture lessons learned and recognise contribution.",
  },
];

const CLAUSE_LIBRARY: {
  standard: ClauseMapping["standard"];
  clause: string;
  title: string;
  stage: string;
}[] = [
  {
    standard: "IATF 16949:2016",
    clause: "9.1.1.1",
    title: "Monitoring and measurement of manufacturing processes",
    stage: "Connected Signal",
  },
  {
    standard: "IATF 16949:2016",
    clause: "5.1.1.1",
    title: "Corporate responsibility",
    stage: "Management Top 5",
  },
  {
    standard: "ISO 9001:2015",
    clause: "7.5.3",
    title: "Control of documented information",
    stage: "Connected Evidence",
  },
  {
    standard: "IATF 16949:2016",
    clause: "10.2.3",
    title: "Problem solving",
    stage: "Governed Problem Record",
  },
  {
    standard: "ISO 9001:2015",
    clause: "8.7",
    title: "Control of nonconforming output",
    stage: "Containment",
  },
  {
    standard: "ISO 9001:2015",
    clause: "10.2.2",
    title: "Corrective action — documented information",
    stage: "Root Cause & Corrective Action",
  },
  {
    standard: "IATF 16949:2016",
    clause: "8.5.6.1",
    title: "Control of changes — supplemental",
    stage: "Engineering / Process Change",
  },
  {
    standard: "IATF 16949:2016",
    clause: "8.3.6",
    title: "Design and development changes",
    stage: "Breakpoint & Implementation",
  },
  {
    standard: "IATF 16949:2016",
    clause: "8.5.1.1",
    title: "Control plan",
    stage: "PFMEA / Control Plan Update",
  },
  {
    standard: "IATF 16949:2016",
    clause: "10.2.4",
    title: "Error-proofing",
    stage: "Effectiveness & Prevention",
  },
  {
    standard: "ISO 9001:2015",
    clause: "9.1.3",
    title: "Analysis and evaluation",
    stage: "Effectiveness & Prevention",
  },
  {
    standard: "IATF 16949:2016",
    clause: "10.3.1",
    title: "Continual improvement — supplemental",
    stage: "Read-Across & Closure",
  },
];

const EVIDENCE_TYPES: EvidenceType[] = [
  "aggregate-analytics",
  "field-aggregate",
  "test-report",
  "supplier-8d",
  "engineering-change",
  "audit-record",
];

export const PROGRAMS: Program[] = [
  {
    id: "prg-ridgeline",
    code: "BV-RL",
    name: "Ridgeline Full-Size Pickup",
    vehicleLine: "T2 Body-on-Frame",
    modelYear: 2026,
    unitsInService: 412_600,
    region: "North America",
    c1000: 18.4,
    c1000Target: 14.0,
    trend: [22.6, 21.8, 21.1, 20.4, 20.9, 20.2, 19.6, 19.1, 19.4, 18.9, 18.6, 18.4].map((v, i) => ({
      month: MONTHS[i] ?? "",
      value: v,
    })),
  },
  {
    id: "prg-meridian",
    code: "BV-MD",
    name: "Meridian Crossover EV",
    vehicleLine: "E3 Skateboard",
    modelYear: 2026,
    unitsInService: 268_300,
    region: "North America / EU",
    c1000: 24.7,
    c1000Target: 16.0,
    trend: [17.2, 18.6, 19.9, 21.4, 22.8, 23.1, 24.0, 24.4, 25.3, 25.1, 24.9, 24.7].map((v, i) => ({
      month: MONTHS[i] ?? "",
      value: v,
    })),
  },
  {
    id: "prg-atlas",
    code: "BV-AT",
    name: "Atlas Medium-Duty Chassis",
    vehicleLine: "C4 Commercial",
    modelYear: 2025,
    unitsInService: 96_400,
    region: "Global",
    c1000: 11.2,
    c1000Target: 12.0,
    trend: [14.4, 13.9, 13.4, 13.1, 12.8, 12.4, 12.2, 11.9, 11.7, 11.5, 11.3, 11.2].map((v, i) => ({
      month: MONTHS[i] ?? "",
      value: v,
    })),
  },
  {
    id: "prg-summit",
    code: "BV-SM",
    name: "Summit Premium SUV",
    vehicleLine: "T2 Body-on-Frame",
    modelYear: 2026,
    unitsInService: 154_900,
    region: "North America",
    c1000: 15.9,
    c1000Target: 13.5,
    trend: [13.1, 13.6, 14.2, 14.8, 15.1, 15.6, 15.4, 15.8, 16.2, 16.0, 15.8, 15.9].map((v, i) => ({
      month: MONTHS[i] ?? "",
      value: v,
    })),
  },
];

interface Core {
  code: string;
  title: string;
  programId: string;
  platform: string;
  component: string;
  failureCause: string;
  supplier: string;
  plant: string;
  status: RecordStatus;
  riskScore: number;
  risk: RiskLevel;
  claims: number;
  unitsInService: number;
  owner: string;
  champion: string;
  stagesComplete: number;
  actualCost: number;
  exposure: number;
  carry: number;
  targetReductionPct: number;
  observedReductionPct: number;
  description: string;
  c1000Start: number;
  c1000End: number;
  openedAt: string;
  targetCloseAt: string;
  pfmea: {
    process: string;
    failureMode: string;
    effect: string;
    cause: string;
    sev: number;
    occ: number;
    det: number;
    occAfter: number;
    detAfter: number;
    control: string;
    method: string;
    freq: string;
    reaction: string;
    rationale: string;
  }[];
  readAcross: {
    platform: string;
    program: string;
    component: string;
    units: number;
    exposure: number;
    status: ReadAcrossItem["status"];
    action: string;
    owner: string;
  }[];
  containment: {
    action: string;
    scope: string;
    owner: string;
    due: string;
    status: ContainmentAction["status"];
  }[];
  recovery: {
    action: string;
    supplier: string;
    amount: number;
    status: RecoveryAction["status"];
  }[];
}

const CORES: Core[] = [
  {
    code: "PR-2601",
    title: "High-voltage battery coolant fitting seepage",
    programId: "prg-meridian",
    platform: "E3 Skateboard",
    component: "HV battery coolant quick-connect",
    failureCause: "Seal compression set from over-torqued fitting",
    supplier: "Northline Thermal Systems",
    plant: "Riverbend Assembly",
    status: "implementation",
    riskScore: 94,
    risk: "critical",
    claims: 2140,
    unitsInService: 268_300,
    owner: "M. Okafor",
    champion: "VP Quality — L. Trent",
    stagesComplete: 8,
    actualCost: 4_820_000,
    exposure: 19_400_000,
    carry: 0.045,
    targetReductionPct: 70,
    observedReductionPct: 48,
    description:
      "Aggregate warranty and telematics signals show progressive coolant loss at the HV battery quick-connect. Seepage triggers thermal derate and, in a small share of cases, a stop-drive event. Containment is complete in plant and dealer stock; a redesigned fitting is running to breakpoint.",
    c1000Start: 3.1,
    c1000End: 9.4,
    openedAt: iso(2026, 1, 12),
    targetCloseAt: iso(2026, 10, 30),
    pfmea: [
      {
        process: "HV coolant line assembly",
        failureMode: "Quick-connect seal seepage",
        effect: "Thermal derate, potential stop-drive",
        cause: "Seal compression set from torque overshoot",
        sev: 9,
        occ: 6,
        det: 5,
        occAfter: 2,
        detAfter: 2,
        control: "Torque-to-angle with in-line transducer",
        method: "100% torque monitoring + error-proofed reject",
        freq: "Every unit",
        reaction: "Quarantine station output, notify line quality, re-verify last 30 units",
        rationale:
          "Occurrence held at 2 rather than 1: field data after breakpoint is only 9 weeks mature.",
      },
      {
        process: "Battery pack leak test",
        failureMode: "Micro-leak not detected",
        effect: "Escape to customer",
        cause: "Pressure-decay threshold too coarse",
        sev: 9,
        occ: 5,
        det: 7,
        occAfter: 3,
        detAfter: 3,
        control: "Helium trace leak test at pack end-of-line",
        method: "100% automated with SPC on decay rate",
        freq: "Every pack",
        reaction: "Hold pack, escalate to pack engineering, sample 5 preceding packs",
        rationale:
          "Detection kept at 3: helium test proven, but gauge R&R study is scheduled and not yet closed.",
      },
    ],
    readAcross: [
      {
        platform: "E3 Skateboard",
        program: "Meridian Crossover EV",
        component: "HV coolant quick-connect",
        units: 268_300,
        exposure: 19_400_000,
        status: "action-open",
        action: "Breakpoint fitting design and torque error-proofing",
        owner: "M. Okafor",
      },
      {
        platform: "E3 Skateboard",
        program: "Meridian Commercial Van (pre-launch)",
        component: "HV coolant quick-connect",
        units: 12_400,
        exposure: 2_100_000,
        status: "in-review",
        action: "Adopt revised fitting before job 1",
        owner: "R. Delgado",
      },
      {
        platform: "C4 Commercial",
        program: "Atlas Medium-Duty Chassis",
        component: "Auxiliary cooling quick-connect",
        units: 96_400,
        exposure: 1_450_000,
        status: "cleared",
        action: "Different supplier and seal geometry — screened out with data",
        owner: "H. Baptiste",
      },
    ],
    containment: [
      {
        action: "100% torque re-verification of HV coolant fittings in plant",
        scope: "Riverbend Assembly",
        owner: "J. Marek",
        due: iso(2026, 1, 20),
        status: "verified",
      },
      {
        action: "Dealer stock inspection campaign with aggregate reporting",
        scope: "1,180 dealers",
        owner: "S. Whitfield",
        due: iso(2026, 2, 6),
        status: "verified",
      },
      {
        action: "Supplier certified-lot shipping with seal compression audit",
        scope: "Northline Thermal Systems",
        owner: "A. Perez",
        due: iso(2026, 2, 14),
        status: "in-progress",
      },
    ],
    recovery: [
      {
        action: "Warranty cost-sharing claim — seal compression set",
        supplier: "Northline Thermal Systems",
        amount: 2_400_000,
        status: "negotiating",
      },
      {
        action: "Sort and rework labour recovery",
        supplier: "Northline Thermal Systems",
        amount: 380_000,
        status: "agreed",
      },
      {
        action: "Certified-lot inspection cost recovery",
        supplier: "Northline Thermal Systems",
        amount: 145_000,
        status: "recovered",
      },
    ],
  },
  {
    code: "PR-2602",
    title: "Tailgate latch actuator intermittent no-release",
    programId: "prg-ridgeline",
    platform: "T2 Body-on-Frame",
    component: "Power tailgate latch actuator",
    failureCause: "Motor brush wear accelerated by contaminated grease lot",
    supplier: "Kestrel Motion Components",
    plant: "Fort Hallow Assembly",
    status: "verification",
    riskScore: 81,
    risk: "critical",
    claims: 3410,
    unitsInService: 412_600,
    owner: "D. Ivers",
    champion: "Dir. Quality — P. Nandy",
    stagesComplete: 10,
    actualCost: 6_150_000,
    exposure: 11_800_000,
    carry: 0.038,
    targetReductionPct: 65,
    observedReductionPct: 61,
    description:
      "Latch actuators from three supplier lots show early brush wear traced to a contaminated grease batch. Aggregate claims cluster tightly by build window. Corrective action implemented at breakpoint; effectiveness measurement is in its second month.",
    c1000Start: 6.2,
    c1000End: 3.1,
    openedAt: iso(2025, 11, 4),
    targetCloseAt: iso(2026, 9, 28),
    pfmea: [
      {
        process: "Actuator motor greasing",
        failureMode: "Contaminated grease applied",
        effect: "Brush wear, no-release",
        cause: "Incoming grease lot not verified for particulate",
        sev: 7,
        occ: 7,
        det: 6,
        occAfter: 2,
        detAfter: 3,
        control: "Incoming particulate count per grease lot",
        method: "Lot-based certificate + on-site verification",
        freq: "Every lot",
        reaction: "Reject lot, quarantine actuators built since last passing lot",
        rationale: "Detection at 3, not 2: verification is lot-based rather than unit-based.",
      },
      {
        process: "Latch end-of-line function test",
        failureMode: "Marginal current draw passes",
        effect: "Escape to customer",
        cause: "Current limit set without wear margin",
        sev: 7,
        occ: 4,
        det: 5,
        occAfter: 3,
        detAfter: 2,
        control: "Current-signature test with wear-margin limits",
        method: "100% automated signature comparison",
        freq: "Every unit",
        reaction: "Fail unit, alert supplier quality, sample audit of shift",
        rationale: "Occurrence at 3 reflects residual variation across two motor sources.",
      },
    ],
    readAcross: [
      {
        platform: "T2 Body-on-Frame",
        program: "Ridgeline Full-Size Pickup",
        component: "Power tailgate latch",
        units: 412_600,
        exposure: 11_800_000,
        status: "action-open",
        action: "Grease lot verification and current-signature limits",
        owner: "D. Ivers",
      },
      {
        platform: "T2 Body-on-Frame",
        program: "Summit Premium SUV",
        component: "Power liftgate latch",
        units: 154_900,
        exposure: 4_300_000,
        status: "action-open",
        action: "Same actuator family — adopt lot verification",
        owner: "K. Sorensen",
      },
      {
        platform: "E3 Skateboard",
        program: "Meridian Crossover EV",
        component: "Frunk latch actuator",
        units: 268_300,
        exposure: 1_900_000,
        status: "in-review",
        action: "Confirm grease specification difference",
        owner: "T. Aguilar",
      },
    ],
    containment: [
      {
        action: "Quarantine and sort suspect actuator lots",
        scope: "Supplier + 2 plants",
        owner: "A. Perez",
        due: iso(2025, 11, 15),
        status: "verified",
      },
      {
        action: "Dealer replacement kit availability with aggregate tracking",
        scope: "North America",
        owner: "S. Whitfield",
        due: iso(2025, 12, 2),
        status: "verified",
      },
    ],
    recovery: [
      {
        action: "Grease supplier chargeback via tier-1",
        supplier: "Kestrel Motion Components",
        amount: 1_950_000,
        status: "agreed",
      },
      {
        action: "Sort and containment labour",
        supplier: "Kestrel Motion Components",
        amount: 265_000,
        status: "recovered",
      },
    ],
  },
  {
    code: "PR-2603",
    title: "Infotainment head unit black screen after OTA update",
    programId: "prg-meridian",
    platform: "E3 Skateboard",
    component: "Center display head unit",
    failureCause: "Flash memory wear-levelling defect under update load",
    supplier: "Aventine Electronics",
    plant: "Software / OTA release",
    status: "analysis",
    riskScore: 88,
    risk: "critical",
    claims: 1780,
    unitsInService: 268_300,
    owner: "R. Delgado",
    champion: "VP Quality — L. Trent",
    stagesComplete: 6,
    actualCost: 2_310_000,
    exposure: 14_600_000,
    carry: 0.052,
    targetReductionPct: 80,
    observedReductionPct: 22,
    description:
      "Black-screen events concentrate on units that received two consecutive OTA packages. Aggregate telemetry points to flash wear-levelling behaviour in one memory die revision. Root cause proof is in progress with the supplier.",
    c1000Start: 1.4,
    c1000End: 6.6,
    openedAt: iso(2026, 3, 2),
    targetCloseAt: iso(2026, 11, 20),
    pfmea: [
      {
        process: "OTA package deployment",
        failureMode: "Flash block exhaustion during update",
        effect: "Head unit no-boot, loss of rear camera display",
        cause: "Wear-levelling firmware defect in memory die rev B",
        sev: 8,
        occ: 6,
        det: 7,
        occAfter: 3,
        detAfter: 3,
        control: "Pre-flight block-health telemetry gate",
        method: "Aggregate fleet health screen before release",
        freq: "Every release",
        reaction: "Halt deployment ring, notify software release board",
        rationale:
          "Detection at 3 pending validation of the health gate over a full release cycle.",
      },
    ],
    readAcross: [
      {
        platform: "E3 Skateboard",
        program: "Meridian Crossover EV",
        component: "Center display head unit",
        units: 268_300,
        exposure: 14_600_000,
        status: "action-open",
        action: "Firmware fix plus deployment health gate",
        owner: "R. Delgado",
      },
      {
        platform: "T2 Body-on-Frame",
        program: "Summit Premium SUV",
        component: "Rear-seat display module",
        units: 154_900,
        exposure: 3_100_000,
        status: "in-review",
        action: "Confirm memory die revision in bill of material",
        owner: "K. Sorensen",
      },
    ],
    containment: [
      {
        action: "Pause OTA deployment rings 3–5",
        scope: "Global fleet",
        owner: "R. Delgado",
        due: iso(2026, 3, 6),
        status: "verified",
      },
      {
        action: "Dealer reflash procedure with aggregate success reporting",
        scope: "1,180 dealers",
        owner: "S. Whitfield",
        due: iso(2026, 4, 1),
        status: "in-progress",
      },
    ],
    recovery: [
      {
        action: "Software defect cost-share claim",
        supplier: "Aventine Electronics",
        amount: 4_200_000,
        status: "submitted",
      },
    ],
  },
  {
    code: "PR-2604",
    title: "Front strut mount noise over cold-weather cycles",
    programId: "prg-summit",
    platform: "T2 Body-on-Frame",
    component: "Front strut upper mount",
    failureCause: "Elastomer formulation change reduced cold-cycle durability",
    supplier: "Halden Ride Systems",
    plant: "Cedar Point Assembly",
    status: "containment",
    riskScore: 72,
    risk: "high",
    claims: 1240,
    unitsInService: 154_900,
    owner: "K. Sorensen",
    champion: "Dir. Quality — P. Nandy",
    stagesComplete: 5,
    actualCost: 1_180_000,
    exposure: 6_900_000,
    carry: 0.041,
    targetReductionPct: 60,
    observedReductionPct: 14,
    description:
      "Cold-climate regions show a step increase in strut mount noise claims after a supplier elastomer reformulation. Containment is running while cold-cycle durability testing reproduces the failure.",
    c1000Start: 2.2,
    c1000End: 8.0,
    openedAt: iso(2026, 2, 18),
    targetCloseAt: iso(2026, 12, 12),
    pfmea: [
      {
        process: "Strut mount moulding",
        failureMode: "Elastomer cold-cycle cracking",
        effect: "Noise, degraded ride isolation",
        cause: "Reformulated compound with lower low-temperature elongation",
        sev: 6,
        occ: 7,
        det: 6,
        occAfter: 3,
        detAfter: 3,
        control: "Compound certification with cold elongation test",
        method: "Per-batch laboratory test",
        freq: "Every batch",
        reaction: "Hold batch, escalate to materials engineering",
        rationale:
          "Occurrence at 3: reverted compound is validated in lab but only 6 weeks in production.",
      },
    ],
    readAcross: [
      {
        platform: "T2 Body-on-Frame",
        program: "Summit Premium SUV",
        component: "Front strut upper mount",
        units: 154_900,
        exposure: 6_900_000,
        status: "action-open",
        action: "Compound revert plus cold-cycle certification",
        owner: "K. Sorensen",
      },
      {
        platform: "T2 Body-on-Frame",
        program: "Ridgeline Full-Size Pickup",
        component: "Front strut upper mount",
        units: 412_600,
        exposure: 9_800_000,
        status: "in-review",
        action: "Same supplier compound — verify part usage window",
        owner: "D. Ivers",
      },
    ],
    containment: [
      {
        action: "Switch to certified pre-reformulation compound stock",
        scope: "Halden Ride Systems",
        owner: "A. Perez",
        due: iso(2026, 3, 1),
        status: "verified",
      },
      {
        action: "Cold-region dealer diagnostic bulletin",
        scope: "Canada, Upper Midwest, Nordics",
        owner: "S. Whitfield",
        due: iso(2026, 3, 18),
        status: "in-progress",
      },
    ],
    recovery: [
      {
        action: "Unapproved change chargeback",
        supplier: "Halden Ride Systems",
        amount: 1_600_000,
        status: "negotiating",
      },
    ],
  },
  {
    code: "PR-2605",
    title: "Fuel pump control module water ingress",
    programId: "prg-ridgeline",
    platform: "T2 Body-on-Frame",
    component: "Fuel pump control module",
    failureCause: "Connector seal seating missed at assembly",
    supplier: "Aventine Electronics",
    plant: "Fort Hallow Assembly",
    status: "implementation",
    riskScore: 76,
    risk: "high",
    claims: 980,
    unitsInService: 412_600,
    owner: "J. Marek",
    champion: "Dir. Quality — P. Nandy",
    stagesComplete: 8,
    actualCost: 2_640_000,
    exposure: 7_400_000,
    carry: 0.036,
    targetReductionPct: 70,
    observedReductionPct: 52,
    description:
      "Water ingress into the fuel pump control module causes no-start events after high-pressure wash or heavy rain. Root cause is a partially seated connector seal at assembly; vision error-proofing is implemented to breakpoint.",
    c1000Start: 3.0,
    c1000End: 1.6,
    openedAt: iso(2025, 12, 9),
    targetCloseAt: iso(2026, 9, 15),
    pfmea: [
      {
        process: "Underbody harness connection",
        failureMode: "Connector seal not fully seated",
        effect: "Water ingress, no-start",
        cause: "No positive seating confirmation at station",
        sev: 8,
        occ: 5,
        det: 6,
        occAfter: 2,
        detAfter: 2,
        control: "Vision seating confirmation with interlock",
        method: "100% vision inspection",
        freq: "Every unit",
        reaction: "Station interlock stops conveyor, quality re-seats and re-verifies",
        rationale: "Occurrence at 2 rather than 1: two-shift operator variation still under study.",
      },
    ],
    readAcross: [
      {
        platform: "T2 Body-on-Frame",
        program: "Ridgeline Full-Size Pickup",
        component: "Fuel pump control module",
        units: 412_600,
        exposure: 7_400_000,
        status: "action-open",
        action: "Vision seating confirmation at station 44",
        owner: "J. Marek",
      },
      {
        platform: "C4 Commercial",
        program: "Atlas Medium-Duty Chassis",
        component: "Fuel pump control module",
        units: 96_400,
        exposure: 1_200_000,
        status: "cleared",
        action: "Different connector family, sealed at supplier",
        owner: "H. Baptiste",
      },
    ],
    containment: [
      {
        action: "Manual seating audit on 100% of units",
        scope: "Fort Hallow Assembly",
        owner: "J. Marek",
        due: iso(2025, 12, 18),
        status: "verified",
      },
    ],
    recovery: [
      {
        action: "Connector supplier design contribution",
        supplier: "Aventine Electronics",
        amount: 420_000,
        status: "negotiating",
      },
    ],
  },
  {
    code: "PR-2606",
    title: "Seat heater element open circuit",
    programId: "prg-summit",
    platform: "T2 Body-on-Frame",
    component: "Front seat heater mat",
    failureCause: "Carbon fibre element fatigue at bolster fold",
    supplier: "Corvale Interiors",
    plant: "Cedar Point Assembly",
    status: "analysis",
    riskScore: 58,
    risk: "medium",
    claims: 640,
    unitsInService: 154_900,
    owner: "T. Aguilar",
    champion: "Dir. Quality — P. Nandy",
    stagesComplete: 6,
    actualCost: 720_000,
    exposure: 3_200_000,
    carry: 0.03,
    targetReductionPct: 55,
    observedReductionPct: 19,
    description:
      "Heater elements open at the bolster fold radius after repeated ingress/egress cycles. Aggregate claims skew to driver seat and to fleet duty cycles.",
    c1000Start: 1.8,
    c1000End: 4.1,
    openedAt: iso(2026, 2, 2),
    targetCloseAt: iso(2026, 11, 6),
    pfmea: [
      {
        process: "Heater mat lamination",
        failureMode: "Element fatigue fracture at fold radius",
        effect: "Loss of seat heating",
        cause: "Fold radius below element minimum bend spec",
        sev: 5,
        occ: 6,
        det: 5,
        occAfter: 3,
        detAfter: 3,
        control: "Fold radius gauge and element routing standard",
        method: "First-off plus hourly check",
        freq: "Hourly",
        reaction: "Hold since last good check, adjust fixture, re-verify",
        rationale: "Detection at 3: sampling-based, not 100% inspection.",
      },
    ],
    readAcross: [
      {
        platform: "T2 Body-on-Frame",
        program: "Summit Premium SUV",
        component: "Front seat heater mat",
        units: 154_900,
        exposure: 3_200_000,
        status: "action-open",
        action: "Element routing standard revision",
        owner: "T. Aguilar",
      },
      {
        platform: "T2 Body-on-Frame",
        program: "Ridgeline Full-Size Pickup",
        component: "Front seat heater mat",
        units: 412_600,
        exposure: 2_400_000,
        status: "in-review",
        action: "Verify bolster geometry difference",
        owner: "D. Ivers",
      },
    ],
    containment: [
      {
        action: "Revised fixture with fold radius control",
        scope: "Corvale Interiors",
        owner: "A. Perez",
        due: iso(2026, 3, 12),
        status: "in-progress",
      },
    ],
    recovery: [
      {
        action: "Design contribution claim",
        supplier: "Corvale Interiors",
        amount: 310_000,
        status: "submitted",
      },
    ],
  },
  {
    code: "PR-2607",
    title: "Onboard charger derate in high ambient conditions",
    programId: "prg-meridian",
    platform: "E3 Skateboard",
    component: "11 kW onboard charger",
    failureCause: "Thermal interface material voiding on power stage",
    supplier: "Northline Thermal Systems",
    plant: "Riverbend Assembly",
    status: "containment",
    riskScore: 79,
    risk: "high",
    claims: 1120,
    unitsInService: 268_300,
    owner: "R. Delgado",
    champion: "VP Quality — L. Trent",
    stagesComplete: 5,
    actualCost: 1_540_000,
    exposure: 9_100_000,
    carry: 0.047,
    targetReductionPct: 65,
    observedReductionPct: 11,
    description:
      "Charging sessions in high ambient regions derate to 6 kW and, in some cases, fault out. Teardown aggregates show voiding in the thermal interface material over the power stage.",
    c1000Start: 1.1,
    c1000End: 4.2,
    openedAt: iso(2026, 4, 8),
    targetCloseAt: iso(2027, 1, 22),
    pfmea: [
      {
        process: "Power stage TIM dispense",
        failureMode: "TIM voiding under power device",
        effect: "Thermal derate, charge fault",
        cause: "Dispense pattern traps air at device corners",
        sev: 7,
        occ: 6,
        det: 7,
        occAfter: 3,
        detAfter: 3,
        control: "Revised dispense pattern with vacuum assist",
        method: "In-line dispense weight plus periodic X-ray",
        freq: "Every unit / hourly X-ray",
        reaction: "Hold since last X-ray pass, purge dispenser, re-verify",
        rationale:
          "Detection at 3: X-ray is sample based; 100% inspection not economically feasible.",
      },
    ],
    readAcross: [
      {
        platform: "E3 Skateboard",
        program: "Meridian Crossover EV",
        component: "11 kW onboard charger",
        units: 268_300,
        exposure: 9_100_000,
        status: "action-open",
        action: "Dispense pattern revision and X-ray sampling",
        owner: "R. Delgado",
      },
      {
        platform: "E3 Skateboard",
        program: "Meridian Commercial Van (pre-launch)",
        component: "22 kW onboard charger",
        units: 12_400,
        exposure: 900_000,
        status: "in-review",
        action: "Shared power stage — confirm dispense process",
        owner: "M. Okafor",
      },
    ],
    containment: [
      {
        action: "X-ray screening of finished charger stock",
        scope: "Riverbend + supplier",
        owner: "A. Perez",
        due: iso(2026, 5, 2),
        status: "verified",
      },
      {
        action: "High-ambient region software derate protection",
        scope: "Global OTA",
        owner: "R. Delgado",
        due: iso(2026, 5, 20),
        status: "in-progress",
      },
    ],
    recovery: [
      {
        action: "Process capability chargeback",
        supplier: "Northline Thermal Systems",
        amount: 1_100_000,
        status: "submitted",
      },
    ],
  },
  {
    code: "PR-2608",
    title: "Brake pedal squeak in humid conditions",
    programId: "prg-atlas",
    platform: "C4 Commercial",
    component: "Brake pedal pivot bushing",
    failureCause: "Bushing lubricant washout",
    supplier: "Kestrel Motion Components",
    plant: "Lakeshore Chassis",
    status: "closed",
    riskScore: 34,
    risk: "low",
    claims: 210,
    unitsInService: 96_400,
    owner: "H. Baptiste",
    champion: "Plant Quality — G. Oyelaran",
    stagesComplete: 11,
    actualCost: 260_000,
    exposure: 480_000,
    carry: 0.022,
    targetReductionPct: 50,
    observedReductionPct: 63,
    description:
      "Pedal pivot squeak in humid climates, resolved with a revised lubricant and bushing material. Closed with effectiveness proven over four months of post-breakpoint data.",
    c1000Start: 2.4,
    c1000End: 0.6,
    openedAt: iso(2025, 9, 15),
    targetCloseAt: iso(2026, 6, 30),
    pfmea: [
      {
        process: "Pedal assembly lubrication",
        failureMode: "Lubricant washout in humidity",
        effect: "Audible squeak, customer dissatisfaction",
        cause: "Non-hydrophobic grease specification",
        sev: 3,
        occ: 6,
        det: 5,
        occAfter: 2,
        detAfter: 2,
        control: "Hydrophobic grease specification with lot certification",
        method: "Lot certification plus assembly audit",
        freq: "Every lot",
        reaction: "Reject lot, quarantine assemblies since last passing lot",
        rationale:
          "Occurrence at 2: retained to reflect residual humidity variation across regions.",
      },
    ],
    readAcross: [
      {
        platform: "C4 Commercial",
        program: "Atlas Medium-Duty Chassis",
        component: "Brake pedal pivot bushing",
        units: 96_400,
        exposure: 480_000,
        status: "cleared",
        action: "Hydrophobic grease standard released",
        owner: "H. Baptiste",
      },
      {
        platform: "T2 Body-on-Frame",
        program: "Ridgeline Full-Size Pickup",
        component: "Brake pedal pivot bushing",
        units: 412_600,
        exposure: 640_000,
        status: "cleared",
        action: "Standard adopted in pedal box specification",
        owner: "D. Ivers",
      },
    ],
    containment: [
      {
        action: "Grease specification change with certified stock",
        scope: "Kestrel Motion Components",
        owner: "A. Perez",
        due: iso(2025, 10, 10),
        status: "verified",
      },
    ],
    recovery: [
      {
        action: "Specification error cost share",
        supplier: "Kestrel Motion Components",
        amount: 90_000,
        status: "recovered",
      },
    ],
  },
  {
    code: "PR-2609",
    title: "Trailer brake controller intermittent fault code",
    programId: "prg-ridgeline",
    platform: "T2 Body-on-Frame",
    component: "Integrated trailer brake controller",
    failureCause: "Solder joint fatigue on current sense shunt",
    supplier: "Aventine Electronics",
    plant: "Fort Hallow Assembly",
    status: "analysis",
    riskScore: 68,
    risk: "high",
    claims: 870,
    unitsInService: 412_600,
    owner: "D. Ivers",
    champion: "Dir. Quality — P. Nandy",
    stagesComplete: 6,
    actualCost: 1_020_000,
    exposure: 5_600_000,
    carry: 0.034,
    targetReductionPct: 60,
    observedReductionPct: 17,
    description:
      "Trailer brake controller sets a fault code intermittently under towing load. Aggregate returned-part analysis points to solder joint fatigue on the current sense shunt under thermal cycling.",
    c1000Start: 1.2,
    c1000End: 2.6,
    openedAt: iso(2026, 3, 20),
    targetCloseAt: iso(2026, 12, 4),
    pfmea: [
      {
        process: "Shunt reflow soldering",
        failureMode: "Solder joint fatigue crack",
        effect: "Intermittent brake controller fault",
        cause: "Insufficient joint volume for thermal cycling load",
        sev: 7,
        occ: 5,
        det: 6,
        occAfter: 3,
        detAfter: 3,
        control: "Stencil revision with paste volume SPC",
        method: "SPI 100% plus AOI",
        freq: "Every board",
        reaction: "Hold board lot, verify stencil, escalate to process engineering",
        rationale:
          "Detection at 3: SPI detects volume, not eventual fatigue; accelerated cycle test still running.",
      },
    ],
    readAcross: [
      {
        platform: "T2 Body-on-Frame",
        program: "Ridgeline Full-Size Pickup",
        component: "Trailer brake controller",
        units: 412_600,
        exposure: 5_600_000,
        status: "action-open",
        action: "Stencil and paste volume revision",
        owner: "D. Ivers",
      },
      {
        platform: "C4 Commercial",
        program: "Atlas Medium-Duty Chassis",
        component: "Trailer brake controller",
        units: 96_400,
        exposure: 980_000,
        status: "in-review",
        action: "Shared board revision — confirm shunt footprint",
        owner: "H. Baptiste",
      },
    ],
    containment: [
      {
        action: "AOI criteria tightened on shunt joints",
        scope: "Aventine Electronics",
        owner: "A. Perez",
        due: iso(2026, 4, 14),
        status: "in-progress",
      },
    ],
    recovery: [
      {
        action: "Board assembly process chargeback",
        supplier: "Aventine Electronics",
        amount: 780_000,
        status: "submitted",
      },
    ],
  },
  {
    code: "PR-2610",
    title: "Air suspension compressor premature wear",
    programId: "prg-summit",
    platform: "T2 Body-on-Frame",
    component: "Air suspension compressor",
    failureCause: "Desiccant saturation from undersized dryer",
    supplier: "Halden Ride Systems",
    plant: "Cedar Point Assembly",
    status: "implementation",
    riskScore: 71,
    risk: "high",
    claims: 520,
    unitsInService: 154_900,
    owner: "K. Sorensen",
    champion: "VP Quality — L. Trent",
    stagesComplete: 8,
    actualCost: 2_100_000,
    exposure: 5_100_000,
    carry: 0.039,
    targetReductionPct: 65,
    observedReductionPct: 44,
    description:
      "Compressor duty cycle in loaded use saturates an undersized desiccant dryer, admitting moisture and accelerating wear. Larger dryer released to breakpoint with a duty-cycle software limit.",
    c1000Start: 2.9,
    c1000End: 1.5,
    openedAt: iso(2025, 12, 1),
    targetCloseAt: iso(2026, 10, 10),
    pfmea: [
      {
        process: "Dryer cartridge assembly",
        failureMode: "Desiccant saturation",
        effect: "Moisture in air circuit, compressor wear",
        cause: "Dryer capacity below in-service duty cycle",
        sev: 6,
        occ: 6,
        det: 6,
        occAfter: 2,
        detAfter: 3,
        control: "Increased dryer capacity plus duty-cycle limit",
        method: "Design change plus software calibration check",
        freq: "Every unit",
        reaction: "Quarantine build, verify cartridge part number",
        rationale: "Detection at 3: saturation is not directly measurable in production.",
      },
    ],
    readAcross: [
      {
        platform: "T2 Body-on-Frame",
        program: "Summit Premium SUV",
        component: "Air suspension compressor",
        units: 154_900,
        exposure: 5_100_000,
        status: "action-open",
        action: "Dryer capacity increase and duty limit",
        owner: "K. Sorensen",
      },
      {
        platform: "C4 Commercial",
        program: "Atlas Medium-Duty Chassis",
        component: "Air suspension compressor",
        units: 96_400,
        exposure: 1_300_000,
        status: "in-review",
        action: "Confirm duty cycle margin in commercial application",
        owner: "H. Baptiste",
      },
    ],
    containment: [
      {
        action: "Field software duty-cycle limit via OTA",
        scope: "North America",
        owner: "K. Sorensen",
        due: iso(2026, 1, 15),
        status: "verified",
      },
    ],
    recovery: [
      {
        action: "Design specification cost share",
        supplier: "Halden Ride Systems",
        amount: 640_000,
        status: "negotiating",
      },
    ],
  },
  {
    code: "PR-2611",
    title: "Windshield acoustic laminate delamination",
    programId: "prg-atlas",
    platform: "C4 Commercial",
    component: "Acoustic laminated windshield",
    failureCause: "Autoclave cycle deviation at glass supplier",
    supplier: "Corvale Interiors",
    plant: "Lakeshore Chassis",
    status: "verification",
    riskScore: 52,
    risk: "medium",
    claims: 180,
    unitsInService: 96_400,
    owner: "H. Baptiste",
    champion: "Plant Quality — G. Oyelaran",
    stagesComplete: 10,
    actualCost: 610_000,
    exposure: 1_400_000,
    carry: 0.026,
    targetReductionPct: 60,
    observedReductionPct: 55,
    description:
      "Edge delamination traced to an autoclave cycle deviation over a nine-day window at the glass supplier. Process controls restored and effectiveness is being confirmed.",
    c1000Start: 1.9,
    c1000End: 0.5,
    openedAt: iso(2025, 10, 20),
    targetCloseAt: iso(2026, 9, 5),
    pfmea: [
      {
        process: "Laminate autoclave cycle",
        failureMode: "Incomplete interlayer bond",
        effect: "Edge delamination, visual defect",
        cause: "Cycle pressure deviation not alarmed",
        sev: 4,
        occ: 5,
        det: 5,
        occAfter: 2,
        detAfter: 2,
        control: "Autoclave recipe lock with alarm on deviation",
        method: "Continuous cycle monitoring with SPC",
        freq: "Every cycle",
        reaction: "Quarantine cycle load, engineering disposition",
        rationale: "Occurrence at 2 pending twelve months of stable autoclave data.",
      },
    ],
    readAcross: [
      {
        platform: "C4 Commercial",
        program: "Atlas Medium-Duty Chassis",
        component: "Acoustic windshield",
        units: 96_400,
        exposure: 1_400_000,
        status: "action-open",
        action: "Recipe lock and deviation alarm",
        owner: "H. Baptiste",
      },
      {
        platform: "T2 Body-on-Frame",
        program: "Summit Premium SUV",
        component: "Acoustic windshield",
        units: 154_900,
        exposure: 1_700_000,
        status: "cleared",
        action: "Different autoclave line — screened with cycle data",
        owner: "K. Sorensen",
      },
    ],
    containment: [
      {
        action: "Quarantine and inspect suspect cycle loads",
        scope: "Corvale Interiors",
        owner: "A. Perez",
        due: iso(2025, 11, 2),
        status: "verified",
      },
    ],
    recovery: [
      {
        action: "Process deviation chargeback",
        supplier: "Corvale Interiors",
        amount: 240_000,
        status: "agreed",
      },
    ],
  },
  {
    code: "PR-2612",
    title: "Rear axle seal weep on commercial duty cycle",
    programId: "prg-atlas",
    platform: "C4 Commercial",
    component: "Rear axle pinion seal",
    failureCause: "Seal lip run-out from machining variation",
    supplier: "Kestrel Motion Components",
    plant: "Lakeshore Chassis",
    status: "signal",
    riskScore: 46,
    risk: "medium",
    claims: 96,
    unitsInService: 96_400,
    owner: "H. Baptiste",
    champion: "Plant Quality — G. Oyelaran",
    stagesComplete: 2,
    actualCost: 180_000,
    exposure: 2_600_000,
    carry: 0.028,
    targetReductionPct: 60,
    observedReductionPct: 0,
    description:
      "An emerging weep signal on high-mileage commercial duty cycles. Machining run-out is the leading hypothesis; the record has entered the management Top 5 review and evidence assembly is starting.",
    c1000Start: 0.3,
    c1000End: 1.0,
    openedAt: iso(2026, 6, 24),
    targetCloseAt: iso(2027, 2, 26),
    pfmea: [
      {
        process: "Pinion bore machining",
        failureMode: "Seal bore run-out out of tolerance",
        effect: "Lubricant weep, potential axle damage",
        cause: "Tool wear compensation interval too long",
        sev: 6,
        occ: 5,
        det: 6,
        occAfter: 4,
        detAfter: 4,
        control: "Run-out gauging with tool wear compensation",
        method: "Sample gauging per shift",
        freq: "Per shift",
        reaction: "Hold since last passing check, compensate tool offset",
        rationale:
          "Occurrence and detection remain at 4: corrective action is not yet defined or implemented.",
      },
    ],
    readAcross: [
      {
        platform: "C4 Commercial",
        program: "Atlas Medium-Duty Chassis",
        component: "Rear axle pinion seal",
        units: 96_400,
        exposure: 2_600_000,
        status: "in-review",
        action: "Screening run-out data across axle sources",
        owner: "H. Baptiste",
      },
    ],
    containment: [
      {
        action: "Increase run-out gauging frequency pending analysis",
        scope: "Lakeshore Chassis",
        owner: "J. Marek",
        due: iso(2026, 9, 30),
        status: "open",
      },
    ],
    recovery: [],
  },
];

function seriesBetween(start: number, end: number, seed: string): SeriesPoint[] {
  const r = rng(seed);
  return MONTHS.map((month, i) => {
    const t = i / (MONTHS.length - 1);
    const base = start + (end - start) * t;
    const noise = (r() - 0.5) * Math.max(base * 0.18, 0.12);
    return { month, value: Math.max(Math.round((base + noise) * 100) / 100, 0) };
  });
}

function evidence(name: string, seed: string, i: number, owner: string): EvidenceItem {
  const r = rng(seed + name);
  const type = EVIDENCE_TYPES[Math.floor(r() * EVIDENCE_TYPES.length)] ?? "aggregate-analytics";
  const day = 3 + Math.floor(r() * 24);
  return {
    id: `${seed}-ev-${i}`,
    name,
    type,
    addedAt: iso(2026, 1 + (i % 8), day),
    addedBy: owner,
    summary: "Aggregate-level evidence package; no VIN-level or individual customer data retained.",
  };
}

function buildStages(core: Core): WorkflowStage[] {
  return WORKFLOW_BLUEPRINT.map((bp, i) => {
    const complete = i < core.stagesComplete;
    const active = i === core.stagesComplete;
    const status: StageStatus = complete ? "complete" : active ? "in-progress" : "not-started";
    const evidenceCount = complete
      ? bp.evidence.length
      : active
        ? Math.max(bp.evidence.length - 1, 0)
        : 0;
    const dueMonth = 1 + ((i * 7) % 12);
    return {
      id: `${core.code}-S${i + 1}`,
      index: i + 1,
      name: bp.name,
      purpose: bp.purpose,
      clause: bp.clause,
      owner: i < 4 ? core.owner : i < 8 ? core.owner : core.champion,
      dueDate: iso(2026, dueMonth, 12 + ((i * 3) % 15)),
      status,
      completedAt: complete ? iso(2026, dueMonth, 8 + (i % 10)) : undefined,
      requiredEvidence: bp.evidence,
      evidence: bp.evidence
        .slice(0, evidenceCount)
        .map((name, j) => evidence(name, `${core.code}-S${i + 1}`, j, core.owner)),
      notes: complete
        ? [
            {
              id: `${core.code}-S${i + 1}-n1`,
              at: iso(2026, dueMonth, 9 + (i % 8)),
              author: core.owner,
              text: `${bp.name} closed with governance review; evidence set verified as aggregate-only.`,
            },
          ]
        : active
          ? [
              {
                id: `${core.code}-S${i + 1}-n1`,
                at: iso(2026, dueMonth, 5 + (i % 6)),
                author: core.owner,
                text: `${bp.name} in work — awaiting the final required evidence before the gate can be closed.`,
              },
            ]
          : [],
    };
  });
}

function buildEightD(core: Core): EightDStage[] {
  const perStage = Math.max(Math.round((core.stagesComplete / WORKFLOW_BLUEPRINT.length) * 8), 0);
  return EIGHT_D.map((d, i) => {
    const complete = i < perStage;
    const active = i === perStage;
    const required = [`${d.id} deliverable — ${d.title}`, `${d.id} review record`];
    return {
      id: d.id,
      title: d.title,
      objective: d.objective,
      owner: i < 3 ? core.owner : i < 6 ? "A. Perez" : core.champion,
      dueDate: iso(2026, 1 + ((i * 2) % 12), 10 + ((i * 5) % 18)),
      status: complete ? "complete" : active ? "in-progress" : "not-started",
      requiredEvidence: required,
      evidence: required
        .slice(0, complete ? 2 : active ? 1 : 0)
        .map((name, j) => evidence(name, `${core.code}-${d.id}`, j, core.owner)),
      notes: complete
        ? [
            {
              id: `${core.code}-${d.id}-n`,
              at: iso(2026, 1 + ((i * 2) % 12), 11 + (i % 10)),
              author: core.owner,
              text: `${d.id} accepted by the champion; deliverable filed against the governed record.`,
            },
          ]
        : [],
    };
  });
}

function buildPfmea(core: Core): PfmeaRow[] {
  return core.pfmea.map((p, i) => ({
    id: `${core.code}-FM${i + 1}`,
    process: p.process,
    failureMode: p.failureMode,
    effect: p.effect,
    cause: p.cause,
    sevBefore: p.sev,
    occBefore: p.occ,
    detBefore: p.det,
    sevAfter: p.sev,
    occAfter: p.occAfter,
    detAfter: p.detAfter,
    missedRatingRationale: p.rationale,
    controlTechnique: p.control,
    controlMethod: p.method,
    frequency: p.freq,
    reactionPlan: p.reaction,
  }));
}

function buildClauses(core: Core): ClauseMapping[] {
  return CLAUSE_LIBRARY.map((c, i) => {
    const stageIndex = WORKFLOW_BLUEPRINT.findIndex((s) => s.name === c.stage);
    let verdict: ClauseMapping["verdict"];
    if (stageIndex < core.stagesComplete) verdict = "Compliant";
    else if (stageIndex === core.stagesComplete) verdict = "Gap";
    else verdict = stageIndex <= 4 && core.risk === "critical" ? "Violated" : "Gap";
    if (core.status === "closed") verdict = "Compliant";
    const rationale =
      verdict === "Compliant"
        ? "Objective evidence is filed against the governed record and verified by the champion."
        : verdict === "Gap"
          ? "Activity is in progress; required evidence is not yet complete for this clause."
          : "Required activity has not started while a critical field risk is active — escalation raised.";
    return {
      id: `${core.code}-CL${i + 1}`,
      standard: c.standard,
      clause: c.clause,
      title: c.title,
      verdict,
      rationale,
      linkedStage: c.stage,
    };
  });
}

function buildFinancial(core: Core): FinancialProfile {
  const r = rng(core.code + "fin");
  let actual = core.actualCost * 0.18;
  const forecast = MONTHS.map((month, i) => {
    const growth = 1 + 0.12 + r() * 0.06;
    actual = Math.min(actual * growth, core.actualCost);
    const known = i <= 8;
    const ai = actual * (1 + 0.08 * (i + 1) * (core.status === "closed" ? 0.25 : 1));
    return {
      month,
      actual: known ? Math.round(actual) : null,
      aiForecast: Math.round(Math.min(ai, core.exposure)),
    };
  });
  return {
    actualCost: core.actualCost,
    exposure: core.exposure,
    monthlyCarryFactor: core.carry,
    monthlyCarryFactorSource: "seeded-demo",
    supplierRecoveryTarget: core.recovery.reduce((a, b) => a + b.amount, 0),
    supplierRecovered: core.recovery
      .filter((x) => x.status === "recovered")
      .reduce((a, b) => a + b.amount, 0),
    targetReductionPct: core.targetReductionPct,
    observedReductionPct: core.observedReductionPct,
    forecast,
  };
}

function buildAudit(core: Core, stages: WorkflowStage[]): AuditEntry[] {
  const entries: AuditEntry[] = [
    {
      id: `${core.code}-A0`,
      at: core.openedAt,
      actor: core.owner,
      action: "Problem Record opened",
      clause: "IATF 16949 10.2.3",
      detail: `${core.code} governed record created from a confirmed aggregate warranty signal.`,
    },
  ];
  stages
    .filter((s) => s.status === "complete")
    .forEach((s, i) => {
      entries.push({
        id: `${core.code}-A${i + 1}`,
        at: s.completedAt ?? s.dueDate,
        actor: s.owner,
        action: `Stage ${s.index} completed — ${s.name}`,
        clause: s.clause.split(" — ")[0] ?? s.clause,
        detail: `Evidence gate satisfied with ${s.evidence.length} required item(s); entry is append-only.`,
      });
    });
  return entries.sort((a, b) => a.at.localeCompare(b.at));
}

function buildRecord(core: Core): ProblemRecord {
  const stages = buildStages(core);
  return applyTimeline({
    id: core.code.toLowerCase(),
    code: core.code,
    title: core.title,
    programId: core.programId,
    platform: core.platform,
    component: core.component,
    failureCause: core.failureCause,
    supplier: core.supplier,
    plant: core.plant,
    status: core.status,
    risk: core.risk,
    riskScore: core.riskScore,
    claims: core.claims,
    unitsInService: core.unitsInService,
    openedAt: core.openedAt,
    targetCloseAt: core.targetCloseAt,
    owner: core.owner,
    champion: core.champion,
    description: core.description,
    c1000: seriesBetween(core.c1000Start, core.c1000End, core.code + "c"),
    imr: seriesBetween(core.c1000Start, core.c1000End, core.code + "imr"),
    stages,
    eightD: alignEightD(stages, buildEightD(core)),
    pfmea: buildPfmea(core),
    clauses: buildClauses(core),
    readAcross: core.readAcross.map((x, i) => ({
      id: `${core.code}-RA${i + 1}`,
      platform: x.platform,
      program: x.program,
      sharedComponent: x.component,
      unitsInService: x.units,
      exposure: x.exposure,
      status: x.status,
      preventionAction: x.action,
      owner: x.owner,
    })),
    financial: buildFinancial(core),
    containment: core.containment.map((c, i) => ({
      id: `${core.code}-CT${i + 1}`,
      action: c.action,
      scope: c.scope,
      owner: c.owner,
      dueDate: c.due,
      status: c.status,
    })),
    recovery: core.recovery.map((c, i) => ({
      id: `${core.code}-RC${i + 1}`,
      action: c.action,
      supplier: c.supplier,
      amount: c.amount,
      status: c.status,
    })),
    audit: buildAudit(core, stages),
  });
}

export function buildSeedData(): WarrantyDataSet {
  return {
    version: DATA_VERSION,
    generatedAt: iso(2026, 9, 1),
    programs: PROGRAMS,
    records: CORES.map(buildRecord),
  };
}

export { MONTHS };

/**
 * M2 — build a new governed Problem Record (standard 11-stage template) from a
 * signal after a human Create PR decision. Stage 1 "Connected Signal" starts in
 * progress; nothing else is pre-completed.
 */
export function buildRecordFromSignal(input: {
  code: string;
  title: string;
  programId: string;
  platform: string;
  component: string;
  failureCause: string;
  supplier: string;
  plant: string;
  severity: number;
  unitsInService: number;
  exposure: number;
  claims: number;
  owner: string;
  description: string;
  c1000: number;
}): ProblemRecord {
  const risk: RiskLevel =
    input.severity >= 5
      ? "critical"
      : input.severity === 4
        ? "high"
        : input.severity === 3
          ? "medium"
          : "low";
  const now = new Date();
  const close = new Date(now);
  close.setMonth(close.getMonth() + 6);
  const record = buildRecord({
    code: input.code,
    title: input.title,
    programId: input.programId,
    platform: input.platform,
    component: input.component,
    failureCause: input.failureCause,
    supplier: input.supplier,
    plant: input.plant,
    status: "signal",
    riskScore: Math.min(99, input.severity * 18 + 5),
    risk,
    claims: input.claims,
    unitsInService: input.unitsInService,
    owner: input.owner,
    champion: "Quality Director (assign)",
    stagesComplete: 0,
    actualCost: Math.round(input.exposure * 0.05),
    exposure: input.exposure,
    carry: 0.08,
    targetReductionPct: 60,
    observedReductionPct: 0,
    description: input.description,
    c1000Start: input.c1000 * 0.7,
    c1000End: input.c1000,
    openedAt: now.toISOString(),
    targetCloseAt: close.toISOString(),
    pfmea: [],
    readAcross: [],
    containment: [],
    recovery: [],
  });
  return {
    ...record,
    stages: record.stages.map((s, i) => (i === 0 ? { ...s, evidence: [], notes: [] } : s)),
    eightD: record.eightD.map((d) => ({
      ...d,
      evidence: [],
      notes: [],
      status: "not-started" as const,
    })),
    audit: [
      {
        id: `${input.code}-A0`,
        at: now.toISOString(),
        actor: input.owner,
        action: "Problem Record created from governed signal decision",
        clause: "IATF 16949 10.2.3",
        detail: `${input.code} created by an authorised human Create PR disposition; originating signal linked.`,
      },
    ],
  };
}

/**
 * Package #2.5 — keeps synthetic D-step dates within their linked stage per
 * DEFAULT_EIGHTD_MAPPING, and moves a stage to in-progress when a linked D
 * step has already started. D dates move earlier; stage dates are untouched.
 */
function alignEightD(stages: WorkflowStage[], steps: EightDStage[]): EightDStage[] {
  return steps.map((d) => {
    const link = DEFAULT_EIGHTD_MAPPING.links.find((l) => l.dSteps.includes(d.id));
    const stage = link && stages.find((s) => s.index === link.stage);
    if (!stage) return d;
    if (d.status !== "not-started" && stage.status === "not-started") stage.status = "in-progress";
    return d.dueDate.slice(0, 10) > stage.dueDate.slice(0, 10)
      ? { ...d, dueDate: stage.dueDate }
      : d;
  });
}
